import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { Member, SpaceOut, UserKind } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { pictureOf, presetField, uploadChosen } from "../../lib/agentPicture.ts";
import { AGENT_PRESETS, type AgentPreset } from "../../lib/agentPresets.ts";
import { orgApi } from "../../lib/paths.ts";
import type { AgentPicture } from "../../lib/pictures.ts";
import { bump, useAction } from "../../lib/resource.ts";
import { Avatar } from "../../ui/Avatar.tsx";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";
import { AgentPictureChooser } from "../../ui/PictureEditor.tsx";
import { PresetPicture } from "../../ui/PresetPicture.tsx";
import { FOCUS } from "../../ui/styles.ts";

/**
 * Known agents to start from (Claude, ChatGPT…), each with its logo: picking one fills in its name and picture
 * (`onPick`), picking it again lets go of it (a name of your own).
 */
export function PresetChips({ value, onPick }: { value: AgentPreset | undefined; onPick: (p?: AgentPreset) => void }) {
  return (
    <Field label={t("agents.preset")} hint={t("agents.preset.hint")} group={true}>
      <div class="flex flex-wrap gap-1.5">
        {AGENT_PRESETS.map((p) => {
          const on = value?.key === p.key;
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(on ? undefined : p)}
              class={`inline-flex items-center gap-1.5 rounded-full border py-0.5 pr-2.5 pl-0.5 text-sm ${on ? "border-blue-600 bg-blue-50 text-blue-900" : "border-zinc-300 text-zinc-800 hover:bg-zinc-50"} ${FOCUS}`}
            >
              <PresetPicture preset={p.picture} size={22} />
              {p.name}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

/** "None" in the chooser: what the member shows without a picture (its kind's icon). */
export const NoPicture = ({ name, kind }: { name: string; kind: UserKind }) => (
  <Avatar name={name} kind={kind} picture={null} size={36} />
);

/** Changing an agent's or integration's name and picture (admins), in a dialog. `onDone` hears it saved. */
export function EditAgent({
  space,
  member,
  onClose,
  onDone,
}: {
  space: SpaceOut;
  member: Member | null;
  onClose: () => void;
  onDone: () => void;
}) {
  return (
    <Dialog
      open={member !== null}
      onClose={onClose}
      title={member ? t("members.editTitle", { name: member.name }) : ""}
    >
      {member ? <EditAgentForm space={space} member={member} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

function EditAgentForm({
  space,
  member,
  onClose,
  onDone,
}: {
  space: SpaceOut;
  member: Member;
  onClose: () => void;
  onDone: () => void;
}) {
  const had = pictureOf(member);
  const [name, setName] = useState(member.name);
  const [picture, setPicture] = useState<AgentPicture>(had);
  const [save, busy] = useAction(async () => {
    const patch = { ...(name.trim() === member.name ? {} : { name }), ...presetField(picture, had) };
    if (Object.keys(patch).length > 0) {
      await api.patch<Member>(orgApi(space, `/members/${member.id}`), patch);
    }
    await uploadChosen(space, member.id, picture);
    bump();
    onDone();
    onClose();
  });
  return (
    <Form class="flex flex-col gap-4" onSubmit={save}>
      <Field label={t("members.agentName")}>
        <Input value={name} onValue={setName} required={true} />
      </Field>
      <AgentPictureChooser
        value={picture}
        onValue={setPicture}
        fallback={<NoPicture name={name} kind={member.kind} />}
      />
      <DialogActions>
        <Button answer="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {t("common.save")}
        </Button>
      </DialogActions>
    </Form>
  );
}
